import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Heart, TrendingUp, Coffee, Mail, MapPin } from 'lucide-react';

const WorkWithUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });
  }, []);
  const values = [
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Passion for Coffee",
      description: "We're looking for people who genuinely love coffee and want to share that passion with others."
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Team Spirit",
      description: "Collaboration and mutual respect are at the heart of everything we do at Iraga."
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Growth Mindset",
      description: "We support continuous learning and provide opportunities for personal and professional development."
    }
  ];

  const positions = [
    {
      title: "Barista",
      type: "Full-time / Part-time",
      description: "Join our team as a barista and craft exceptional coffee experiences for our customers. Experience with espresso machines preferred but not required—we provide training.",
      requirements: [
        "Passion for coffee and hospitality",
        "Good communication skills",
        "Ability to work in a fast-paced environment",
        "Willingness to learn and grow"
      ]
    },
    {
      title: "Roaster Assistant",
      type: "Full-time",
      description: "Work alongside our head roaster to learn the art of coffee roasting. Help maintain our quality standards and explore the world of specialty coffee.",
      requirements: [
        "Interest in coffee roasting",
        "Attention to detail",
        "Physical ability to lift 25kg bags",
        "Flexible schedule including weekends"
      ]
    },
    {
      title: "Cafe Manager",
      type: "Full-time",
      description: "Lead our cafe team and ensure smooth daily operations. Oversee staff, maintain quality standards, and create a welcoming atmosphere for customers.",
      requirements: [
        "2+ years of cafe/restaurant management experience",
        "Strong leadership and communication skills",
        "Knowledge of coffee and cafe operations",
        "Problem-solving abilities"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/work-with-us-hero.jpg)',
            filter: 'brightness(0.6)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4" data-aos="fade-up">
          <h1 className="text-4xl md:text-7xl font-bold mb-6">Work With Us</h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto">
            Join our passionate team and be part of something special. Build your career in specialty coffee with Iraga.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: '#31415A' }}>Why Join Iraga?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're more than just a coffee shop—we're a community passionate about quality coffee and genuine hospitality.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-shadow" data-aos="fade-up" data-aos-delay={index * 100}>
                <CardHeader>
                  <div className="flex justify-center mb-4" style={{ color: '#31415A' }}>
                    {value.icon}
                  </div>
                  <CardTitle className="text-2xl mb-2" style={{ color: '#31415A' }}>
                    {value.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-gray-600">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#31415A' }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Ready to Join Our Team?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Take the first step towards a rewarding career in specialty coffee. We can't wait to meet you.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WorkWithUs;
